import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { get } from "radash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import AuthService from "src/services/authService";
import UserService from "src/services/userService";
import { useQuery } from "src/services/utils";
import { injectAuthUser } from "src/store/actions/auth";

import SleepCard from "@containers/Cards/SleepCard";
import PasswordResetForm from "@containers/Forms/PasswordResetForm";
import EmailVerificationForm from "@containers/Forms/RegistrationForm/EmailVerificationForm";

const PasswordReset = () => {
  const query = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const [linkSent, setLinkSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const step = query.get("step") || "0";
  const email = query.get("email") || "";
  const code = query.get("code") || "";
  const skipVerification = query.get("setup") || false;

  useEffect(() => {
    const auth = AuthService.getAuth();
    const setupPassword = get(auth, "user.setupPassword", false);
    if (!setupPassword && auth?.token && step === "2") {
      history.replace("/patientDashboardPage");
    }
  }, [step]);

  const sendLink = (email: string) => {
    UserService.sendVerificationCode(email, "resetPassword", "email")
      .then(() => {
        setLinkSent(true);
      })
      .catch((e) =>
        toast.error(
          e.message || "Unable to send reset link, please try again!",
        ),
      );
  };
  const resetPassword = (password: string) => {
    setLoading(true);
    let passwordUpdate = skipVerification
      ? UserService.updatePassword
      : UserService.forgotPassword;
    passwordUpdate({
      email,
      password,
      verificationCode: code,
    })
      .then(() => AuthService.login({ email, password }))
      .then((rsp) => {
        toast.success("Your password has been reset successfully!");
        AuthService.saveAuth(rsp.data);
        dispatch(injectAuthUser(rsp.data.user));
        setTimeout(() => {
          history.replace("/patientDashboardPage");
        }, 2000);
      })
      .catch((e) => {
        toast.error(e.message || "Unable to reset password, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SleepCard />
        </Grid>

        {step === "0" && (
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontSize={32} fontWeight={600}>
              Forgot Your Password
            </Typography>
            <Typography variant="body1" pb={0.5}>
              Please enter the email address associated with your account. If an
              account exists, a verification code will be sent to your email
              inbox or phone.
            </Typography>
            {linkSent && (
              <Alert severity="success" icon={false}>
                An email containing a link to reset your password has been sent
                to your email inbox. Be sure to reset within the next 10 minutes
                otherwise the link will expire.
              </Alert>
            )}
            <EmailVerificationForm
              submitBtnTitle="Send Link"
              submitBtnProps={{ sx: { width: "fit-content" } }}
              format={{
                buttonsDirection: "row-reverse",
              }}
              onSubmit={(data) => sendLink(data.email)}
            />
          </Grid>
        )}

        {step === "2" && (
          <Grid item xs={12} md={6}>
            {" "}
            <Typography variant="h5" fontSize={32} fontWeight={600}>
              Reset Password
            </Typography>
            <Typography variant="body1" pb={0.5}>
              To reset your password, please enter and confirm your new password
            </Typography>
            <Typography variant="body1">
              Request another reset link{" "}
              <Link
                href="/resetPasswordPage"
                variant="body1"
                underline="none"
                sx={{ fontSize: "inherit" }}
              >
                here
              </Link>
            </Typography>
            <PasswordResetForm
              onSubmit={(data) => resetPassword(data.password)}
              submitBtnTitle="Reset Password"
              submitBtnProps={{ sx: { width: "fit-content" }, loading }}
              format={{
                buttonsDirection: "row-reverse",
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PasswordReset;
